import { subtractSecondsFromNow } from '@pff-consumer/utils/lib/date/time'
import { HistoricalPerformanceToggleOptions } from '@pff-consumer/schema'
import { getSelectedPlayerMatchups } from '@pff-consumer/utils'
import { playerProfileSlice } from '../../lib/player-profile/player-profile.slice'
import { consumerApi } from '../../lib/consumer-api/consumer-api'
import { merlinApi } from '../../lib/merlin-api/merlin-api'
import type { RootState, AppStartListening } from '../store.player-profile'

export const addPlayerProfileListener = (startListening: AppStartListening) => {
  startListening({
    matcher: consumerApi.endpoints.getPlayerProfileBaseData.matchFulfilled,
    effect: async (action, listenerApi) => {
      // Can cancel other running instances
      listenerApi.cancelActiveListeners()

      const { player, currentEventWeek, career } = action.payload
      listenerApi.dispatch(playerProfileSlice.actions.updateSelectedPlayer(player))

      listenerApi.dispatch(playerProfileSlice.actions.updateCurrentGameWeek(currentEventWeek))

      listenerApi.dispatch(playerProfileSlice.actions.updateCareer(career))
      listenerApi.dispatch(playerProfileSlice.actions.updatePlayerActiveSeasons(career?.activeSeasons || []))

      // reset matchups to initial state
      listenerApi.dispatch(playerProfileSlice.actions.updateIsMatchupsDataAvailable(false))
      // reset toggle to initial state
      listenerApi.dispatch(
        playerProfileSlice.actions.updateHistoricalPerformanceSelectedToggle(HistoricalPerformanceToggleOptions.AvgL5)
      )
    },
  })

  startListening({
    matcher: consumerApi.endpoints.getPlayerProfileHistoricalPerformanceData.matchFulfilled,
    effect: async (action, listenerApi) => {
      // Can cancel other running instances
      listenerApi.cancelActiveListeners()
      listenerApi.dispatch(playerProfileSlice.actions.updatePlayerProfileHistoricalPerformanceResults(action.payload))
    },
  })

  startListening({
    matcher: consumerApi.endpoints.getSDKMatchups.matchFulfilled,
    effect: async (action, listenerApi) => {
      // Can cancel other running instances
      listenerApi.cancelActiveListeners()
      const state = listenerApi.getState() as RootState
      const selectedPlayerId = state['player-profile'].selectedPlayer?.playerId
      if (selectedPlayerId) {
        const selectedPlayerMatchups = getSelectedPlayerMatchups(action.payload.matchups, selectedPlayerId)
        listenerApi.dispatch(playerProfileSlice.actions.updateIsMatchupsDataAvailable(!!selectedPlayerMatchups))
      }
    },
  })

  startListening({
    matcher: consumerApi.endpoints.getRankings.matchFulfilled,
    effect: async (action, listenerApi) => {
      // Can cancel other running instances
      listenerApi.cancelActiveListeners()
      listenerApi.dispatch(playerProfileSlice.actions.updateFantasyRankings(action.payload))
    },
  })

  startListening({
    matcher: consumerApi.endpoints.getPlayerInjuryStatus.matchFulfilled,
    effect: async (action, listenerApi) => {
      // Can cancel other running instances
      listenerApi.cancelActiveListeners()
      listenerApi.dispatch(playerProfileSlice.actions.updateInjuryData(action.payload))
    },
  })

  startListening({
    matcher: merlinApi.endpoints.getPlayerNews.matchFulfilled,
    effect: async (action, listenerApi) => {
      // Can cancel other running instances
      listenerApi.cancelActiveListeners()
      // Copy data from API store to player profile slice
      listenerApi.dispatch(playerProfileSlice.actions.updateLatestNews(action.payload))
    },
  })

  startListening({
    matcher: consumerApi.endpoints.getGameScripts.matchFulfilled,
    effect: async (action, listenerApi) => {
      // Can cancel other running instances
      listenerApi.cancelActiveListeners()
      listenerApi.dispatch(playerProfileSlice.actions.updateGameScripts(action.payload))
    },
  })

  startListening({
    matcher: consumerApi.endpoints.getGamesByWeek.matchFulfilled,
    effect: async (action, listenerApi) => {
      // Can cancel other running instances
      listenerApi.cancelActiveListeners()
      listenerApi.dispatch(playerProfileSlice.actions.updateNextGame(action.payload))
    },
  })

  startListening({
    matcher: consumerApi.endpoints.getWeekProjection.matchFulfilled,
    effect: async (action, listenerApi) => {
      // Can cancel other running instances
      listenerApi.cancelActiveListeners()
      listenerApi.dispatch(playerProfileSlice.actions.updateWeekProjections(action.payload.projection))
    },
  })

  startListening({
    matcher: consumerApi.endpoints.getPremiumStatsPreviewKeyStats.matchFulfilled,
    effect: async (action, listenerApi) => {
      // Can cancel other running instances
      listenerApi.cancelActiveListeners()

      // @ts-ignore
      const cacheAge = action?.meta?.baseQueryMeta?.response?.headers?.get('Age') as number | undefined
      const updatedAt = subtractSecondsFromNow(cacheAge)
      const updatedAtLabel = updatedAt.toLocaleDateString()
      listenerApi.dispatch(playerProfileSlice.actions.setPageUpdatedAt({ pageUpdatedAt: updatedAtLabel }))
    },
  })
}
